import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import { getNews, getPricesRange } from "../lib/api-modules/market-data.js";
import { listAlerts } from "../lib/api-modules/alerts.js";
import {
  describeRule,
  listSignalRules,
} from "../lib/api-modules/signal-rules.js";
import {
  ALL_HOLDINGS_TICKER,
  type Alert,
  type DailyBar,
  type IndicatorMacdPoint,
  type IndicatorScalarSeries,
  type NewsArticle,
  type PricesRangeResponse,
  type SignalRule,
} from "../lib/api-modules/types.js";
import { etTodayIsoDate, startDateForWindow } from "../lib/util/dates.js";
import { chartThemeOptions } from "../lib/util/chart-theme.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { PositionSizer } from "../components/position-sizer.js";
import { usePreferences } from "../lib/preferences.js";

/**
 * /chart/:ticker — single-ticker daily candlestick view with a news
 * sidebar and toggleable technical-indicator overlays.
 *
 * Window selector (3M/1Y/2Y) drives both the chart query and what's
 * visible — all bars come from the daily `prices` table.
 *
 * Indicators (Phase 2 Session 2.3):
 *   - SMA / EMA / Bollinger Bands / VWAP overlay the candle pane.
 *   - RSI, MACD, ATR each render in their own sub-pane via
 *     lightweight-charts v5 `addSeries(..., paneIndex)`.
 *   - Selection lives in component state for v1; URL-state is a
 *     2.4+ polish task. Default-on: SMA-20 + SMA-50 only.
 *
 * Day change is computed from the last two bars in the response, so
 * the chart and the header are guaranteed to agree.
 */

type ChartWindow = "3M" | "1Y" | "2Y";
const WINDOWS: ChartWindow[] = ["3M", "1Y", "2Y"];

const UP_COLOR = "#10b981"; // emerald-500
const DOWN_COLOR = "#ef4444"; // red-500

// === Indicator catalog ==================================================

export type IndicatorKey =
  | "sma_20"
  | "sma_50"
  | "sma_200"
  | "ema_20"
  | "ema_50"
  | "ema_200"
  | "rsi_14"
  | "macd_12_26_9"
  | "bb_20_2"
  | "atr_14"
  | "vwap";

const DEFAULT_SELECTED: readonly IndicatorKey[] = ["sma_20", "sma_50"];

interface IndicatorSpec {
  key: IndicatorKey;
  label: string;
  group: "MA" | "VOL" | "OSC";
  color: string;
}

const INDICATOR_SPECS: readonly IndicatorSpec[] = [
  { key: "sma_20", label: "SMA-20", group: "MA", color: "#f59e0b" }, // amber-500
  { key: "sma_50", label: "SMA-50", group: "MA", color: "#3b82f6" }, // blue-500
  { key: "sma_200", label: "SMA-200", group: "MA", color: "#8b5cf6" }, // violet-500
  { key: "ema_20", label: "EMA-20", group: "MA", color: "#fbbf24" }, // amber-400
  { key: "ema_50", label: "EMA-50", group: "MA", color: "#60a5fa" }, // blue-400
  { key: "ema_200", label: "EMA-200", group: "MA", color: "#a78bfa" }, // violet-400
  { key: "bb_20_2", label: "BB-20-2", group: "MA", color: "#71717a" }, // zinc-500
  { key: "vwap", label: "VWAP", group: "VOL", color: "#06b6d4" }, // cyan-500
  { key: "rsi_14", label: "RSI-14", group: "OSC", color: "#6366f1" }, // indigo-500
  { key: "macd_12_26_9", label: "MACD", group: "OSC", color: "#3b82f6" },
  { key: "atr_14", label: "ATR-14", group: "OSC", color: "#f43f5e" }, // rose-500
];

// Pane assignments: 0 = candle/overlay; 1+ = sub-panes (one per
// own-pane indicator). Volume sits on pane 0 with its own price scale.
const PANE_RSI = 1;
const PANE_MACD = 2;
const PANE_ATR = 3;

export function ChartPage() {
  const params = useParams();
  const ticker = (params["ticker"] ?? "").toUpperCase();
  const [windowSel, setWindowSel] = useState<ChartWindow>("3M");
  const [selected, setSelected] = useState<Set<IndicatorKey>>(
    () => new Set(DEFAULT_SELECTED),
  );

  if (!ticker) {
    return (
      <PageShell>
        <ErrorMessage>No ticker specified.</ErrorMessage>
      </PageShell>
    );
  }

  const toggleIndicator = (key: IndicatorKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <PageShell>
      <ChartHeader
        ticker={ticker}
        windowSel={windowSel}
        onChangeWindow={setWindowSel}
        selected={selected}
      />
      <IndicatorToggleBar selected={selected} onToggle={toggleIndicator} />
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-4">
        <div className="md:col-span-3">
          <ChartPanel
            ticker={ticker}
            windowSel={windowSel}
            selected={selected}
          />
        </div>
        <aside className="md:col-span-1 space-y-4">
          <PositionSizerPanel
            ticker={ticker}
            windowSel={windowSel}
            selected={selected}
          />
          <RulesPanel ticker={ticker} />
          <NewsSidebar ticker={ticker} />
        </aside>
      </div>
    </PageShell>
  );
}

/**
 * Sidebar panel listing the user's armed rules that apply to the
 * current ticker. Shows direction, current armed/fired state, and a
 * one-line rule summary; clicking opens the /alerts page focused on
 * that rule.
 *
 * "Apply to this ticker" includes both:
 *   - Rules with `mode === "single"` and `ticker` matching exactly.
 *   - Rules with `mode === "all_holdings"` (the rule covers every
 *     ticker the household holds, including this one if it's in the
 *     portfolio — the per-chart filter shows them since the user is
 *     looking at this name).
 */
function RulesPanel({ ticker }: { ticker: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["signal-rules"],
    queryFn: listSignalRules,
  });
  const matching = useMemo(() => {
    if (!data) return [];
    return data.signal_rules.filter(
      (r) =>
        r.is_armed !== false &&
        r.is_archived !== true &&
        (r.ticker === ticker || r.ticker === ALL_HOLDINGS_TICKER),
    );
  }, [data, ticker]);

  return (
    <section
      aria-labelledby="rules-panel-heading"
      className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3
          id="rules-panel-heading"
          className="text-sm font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400"
        >
          Rules on {ticker}
        </h3>
        <Link
          to="/alerts"
          className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          Manage
        </Link>
      </div>
      {isPending && (
        <p className="text-xs text-zinc-500">Loading rules…</p>
      )}
      {isError && (
        <p className="text-xs text-red-600 dark:text-red-400">
          Couldn't load rules.
        </p>
      )}
      {data && matching.length === 0 && (
        <div className="text-xs text-zinc-500">
          <p className="mb-2">No armed rules on this ticker.</p>
          <Link
            to="/alerts"
            className="text-brand-500 hover:underline"
          >
            Create one →
          </Link>
        </div>
      )}
      {matching.length > 0 && (
        <ul className="space-y-2">
          {matching.map((r) => (
            <RuleRow key={r.signal_rule_id} rule={r} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RuleRow({ rule }: { rule: SignalRule }) {
  const direction = rule.direction ?? "info";
  const dirBadge =
    direction === "buy"
      ? { label: "Buy", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" }
      : direction === "sell"
        ? { label: "Sell", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" }
        : { label: "Info", cls: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300" };
  const lastFiredText = formatLastFiredShort(rule);
  return (
    <li className="rounded-md border border-zinc-200 px-2 py-2 text-xs dark:border-zinc-800">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-1.5">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${dirBadge.cls}`}>
              {dirBadge.label}
            </span>
            <span className="truncate font-medium text-zinc-800 dark:text-zinc-200">
              {rule.name}
            </span>
          </div>
          <p className="text-zinc-500 dark:text-zinc-400">
            {describeRule(rule.rule)}
          </p>
          {rule.ticker === ALL_HOLDINGS_TICKER && (
            <p className="mt-0.5 text-[10px] uppercase tracking-wide text-zinc-400">
              All holdings
            </p>
          )}
        </div>
      </div>
      {lastFiredText && (
        <p className="mt-1 text-[11px] text-zinc-500">
          Last fired: {lastFiredText}
        </p>
      )}
    </li>
  );
}

function formatLastFiredShort(rule: SignalRule): string | null {
  const iso = rule.last_fired_at;
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Wraps the position-sizer with a price-query subscription so the
 * latest close + ATR-14 are read from the same cache the chart uses.
 * `usePricesQuery` always requests `atr_14` regardless of selection
 * (decoupled from rendering — see comment in the hook), so the sizer
 * always has live ATR data even when the user has the ATR pane
 * toggled off.
 */
function PositionSizerPanel({
  ticker,
  windowSel,
  selected,
}: {
  ticker: string;
  windowSel: ChartWindow;
  selected: Set<IndicatorKey>;
}) {
  const { data } = usePricesQuery(ticker, windowSel, selected);
  const latestClose = useMemo(() => {
    const last = data?.bars?.[data.bars.length - 1];
    return typeof last?.close === "number" ? last.close : null;
  }, [data?.bars]);
  const latestAtr14 = useMemo(() => {
    const series = data?.indicators?.atr_14;
    if (!series) return null;
    for (let i = series.length - 1; i >= 0; i -= 1) {
      const v = series[i];
      if (typeof v === "number") return v;
    }
    return null;
  }, [data?.indicators?.atr_14]);
  return (
    <PositionSizer
      ticker={ticker}
      latestClose={latestClose}
      latestAtr14={latestAtr14}
    />
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-6xl p-6">{children}</div>;
}

// === Header (ticker + last close + day change + window selector) =========

function ChartHeader({
  ticker,
  windowSel,
  onChangeWindow,
  selected,
}: {
  ticker: string;
  windowSel: ChartWindow;
  onChangeWindow: (w: ChartWindow) => void;
  selected: Set<IndicatorKey>;
}) {
  const { data } = usePricesQuery(ticker, windowSel, selected);
  const summary = useMemo(() => deriveDaySummary(data?.bars), [data?.bars]);

  return (
    <section
      aria-labelledby="chart-heading"
      className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
    >
      <div>
        <h2 id="chart-heading" className="text-3xl font-semibold tracking-tight">
          {ticker}
        </h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          {summary
            ? `Close $${summary.close.toFixed(2)} · ${summary.changeStr} ${summary.percentStr}`
            : "—"}
        </p>
      </div>
      <div role="tablist" aria-label="Chart window" className="inline-flex rounded-md border border-zinc-300 dark:border-zinc-700">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            role="tab"
            aria-selected={w === windowSel}
            onClick={() => onChangeWindow(w)}
            className={`px-3 py-1.5 text-sm ${
              w === windowSel
                ? "bg-brand-500 text-white"
                : "hover:bg-zinc-50 dark:hover:bg-zinc-900"
            } first:rounded-l-md last:rounded-r-md`}
          >
            {w}
          </button>
        ))}
      </div>
    </section>
  );
}

interface DaySummary {
  close: number;
  changeStr: string;
  percentStr: string;
}

export function deriveDaySummary(
  bars: DailyBar[] | undefined,
): DaySummary | null {
  if (!bars || bars.length === 0) return null;
  const last = bars[bars.length - 1];
  if (!last) return null;
  if (bars.length < 2) {
    return { close: last.close, changeStr: "—", percentStr: "" };
  }
  const prev = bars[bars.length - 2];
  if (!prev) return null;
  const diff = last.close - prev.close;
  const pct = prev.close === 0 ? 0 : (diff / prev.close) * 100;
  const sign = diff >= 0 ? "+" : "";
  return {
    close: last.close,
    changeStr: `${sign}${diff.toFixed(2)}`,
    percentStr: `(${sign}${pct.toFixed(2)}%)`,
  };
}

// === Indicator toggle bar ==============================================

function IndicatorToggleBar({
  selected,
  onToggle,
}: {
  selected: Set<IndicatorKey>;
  onToggle: (k: IndicatorKey) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Indicator overlays"
      className="mt-4 flex flex-wrap gap-1 rounded-md border border-zinc-300 p-1 dark:border-zinc-700"
    >
      {INDICATOR_SPECS.map((spec) => {
        const on = selected.has(spec.key);
        return (
          <button
            key={spec.key}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(spec.key)}
            className={`rounded px-2 py-1 text-xs font-medium transition ${
              on
                ? "bg-brand-500 text-white"
                : "text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
            }`}
          >
            {spec.label}
          </button>
        );
      })}
    </div>
  );
}

// === Chart panel (lightweight-charts) ====================================

function ChartPanel({
  ticker,
  windowSel,
  selected,
}: {
  ticker: string;
  windowSel: ChartWindow;
  selected: Set<IndicatorKey>;
}) {
  const { data, isPending, isError, error } = usePricesQuery(
    ticker,
    windowSel,
    selected,
  );
  const { theme } = usePreferences();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  // v5+ markers plugin attached to the candle series; lazily created
  // on mount alongside the candle series itself.
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  // Tracks the indicator series we've added so we can remove them
  // when the user toggles the indicator off (or when the data
  // refreshes for a new window).
  const indicatorSeriesRef = useRef<
    Map<IndicatorKey, ISeriesApi<"Line" | "Histogram">[]>
  >(new Map());
  // Tracks priceLines we've added per (signal_rule_id, indicator_key)
  // so we can remove them when rules change without disturbing the
  // candle/indicator series themselves.
  const ruleLinesRef = useRef<
    Map<string, { series: ISeriesApi<"Line" | "Histogram"> | ISeriesApi<"Candlestick">; line: IPriceLine }>
  >(new Map());

  // Phase 15.5 — pull armed rules + recent fired alerts for this
  // ticker. Rules drive the threshold-line overlays; fired alerts
  // drive the historical markers on the candle series.
  const { data: rulesResp } = useQuery({
    queryKey: ["signal-rules"],
    queryFn: listSignalRules,
  });
  const rulesForTicker = useMemo(() => {
    if (!rulesResp) return [] as SignalRule[];
    return rulesResp.signal_rules.filter(
      (r) =>
        r.is_armed !== false &&
        r.is_archived !== true &&
        (r.ticker === ticker || r.ticker === ALL_HOLDINGS_TICKER),
    );
  }, [rulesResp, ticker]);

  const alertsLookbackDays = lookbackDaysForWindow(windowSel);
  const { data: alertsResp } = useQuery({
    queryKey: ["alerts", "signal_fired", ticker, alertsLookbackDays],
    queryFn: () =>
      listAlerts({
        type: "signal_fired",
        days: alertsLookbackDays,
        ticker,
      }),
  });

  // Mount: create chart + candle + volume. Cleanup: chart.remove()
  // (mandatory — lightweight-charts allocates DOM/canvas that React
  // doesn't track).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const themed = chartThemeOptions(theme);
    const chart = createChart(el, {
      ...themed,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        ...themed.layout,
      },
      timeScale: { ...themed.timeScale, timeVisible: false },
      autoSize: true,
    });
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      borderVisible: false,
      wickUpColor: UP_COLOR,
      wickDownColor: DOWN_COLOR,
    });
    // Volume on a separate price scale, occupying the bottom 25% of the
    // candle pane so candles stay readable.
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.75, bottom: 0 },
    });
    chartRef.current = chart;
    candleRef.current = candleSeries;
    volumeRef.current = volumeSeries;
    markersRef.current = createSeriesMarkers(candleSeries, []);

    return () => {
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volumeRef.current = null;
      markersRef.current = null;
      indicatorSeriesRef.current.clear();
    };
    // Mount-only: theme re-application happens in the dedicated effect
    // below so the chart instance survives a theme toggle.
  }, []);

  // Re-apply theme options whenever the user toggles light/dark. The
  // canvas paints inherent colors imperatively, so a CSS-variable swap
  // alone wouldn't reach the chart's text + gridlines.
  useEffect(() => {
    chartRef.current?.applyOptions(chartThemeOptions(theme));
  }, [theme]);

  // Update candle + volume data when bars change.
  useEffect(() => {
    const candle = candleRef.current;
    const volume = volumeRef.current;
    if (!candle || !volume || !data) return;
    candle.setData(
      data.bars.map((b) => ({
        time: b.date,
        open: b.open ?? b.close,
        high: b.high ?? b.close,
        low: b.low ?? b.close,
        close: b.close,
      })),
    );
    volume.setData(
      data.bars.map((b) => ({
        time: b.date,
        value: b.volume ?? 0,
        color:
          (b.open ?? b.close) <= b.close ? `${UP_COLOR}80` : `${DOWN_COLOR}80`,
      })),
    );
    chartRef.current?.timeScale().fitContent();
  }, [data]);

  // Indicator-render effect: diff active series against `selected`,
  // add new series, remove deselected, update data on the rest. After
  // (re)creating each series, attach any threshold-rule priceLines
  // whose indicator matches.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !data) return;
    const active = indicatorSeriesRef.current;
    const ruleLines = ruleLinesRef.current;

    // Remove rule-lines that belong to series we're about to drop or
    // re-create. Easier to wipe + re-attach than to track diff per
    // (rule_id, indicator).
    for (const { series, line } of ruleLines.values()) {
      try {
        series.removePriceLine(line);
      } catch {
        // Series may already be gone; ignore.
      }
    }
    ruleLines.clear();

    // Remove series the user has toggled off.
    for (const [key, seriesList] of active) {
      if (!selected.has(key)) {
        for (const s of seriesList) chart.removeSeries(s);
        active.delete(key);
      }
    }

    // Add series the user has toggled on (or refresh data for ones
    // already active — simpler to remove + re-add than to track
    // bar-shape changes).
    const indicators = data.indicators;
    for (const key of selected) {
      const existing = active.get(key);
      if (existing) {
        for (const s of existing) chart.removeSeries(s);
        active.delete(key);
      }
      if (!indicators) continue;
      const created = createIndicatorSeries(chart, key, data.bars, indicators);
      if (created.length > 0) active.set(key, created);
    }

    // Phase 15.5 — attach threshold-rule priceLines to the
    // first-created series for each indicator that has a matching
    // armed rule. Only render lines for indicators that are
    // currently visible — otherwise the line would be on a hidden
    // pane and confuse the user.
    for (const rule of rulesForTicker) {
      if (rule.rule.kind !== "threshold") continue;
      const indicatorKey = rule.rule.indicator;
      if (!selected.has(indicatorKey)) continue;
      const seriesList = active.get(indicatorKey);
      const target = seriesList?.[0];
      if (!target) continue;
      const direction = rule.direction ?? "info";
      const color =
        direction === "buy"
          ? UP_COLOR
          : direction === "sell"
            ? DOWN_COLOR
            : "#a1a1aa";
      const line = target.createPriceLine({
        price: rule.rule.value,
        color,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: rule.name.slice(0, 24),
      });
      ruleLines.set(rule.signal_rule_id, { series: target, line });
    }
  }, [data, selected, rulesForTicker]);

  // Phase 15.5 — paint historical fire markers on the candle series.
  // Re-runs whenever the alerts list or the rendered bars change.
  useEffect(() => {
    const plugin = markersRef.current;
    if (!plugin) return;
    if (!data || !alertsResp) {
      plugin.setMarkers([]);
      return;
    }
    // Lightweight-charts requires marker times to match an existing
    // bar's time. Build a Set of bar dates to filter alerts that fall
    // outside the visible range (e.g. weekend timestamps, alerts
    // before the current window).
    const barDates = new Set(data.bars.map((b) => b.date));
    const markers: SeriesMarker<Time>[] = [];
    for (const a of alertsResp.alerts) {
      if (a.alert_type !== "signal_fired") continue;
      const date = a.timestamp.slice(0, 10);
      if (!barDates.has(date)) continue;
      markers.push(buildFireMarker(a, date));
    }
    // Markers must be sorted by time ascending per lightweight-charts.
    markers.sort((m1, m2) =>
      (m1.time as string).localeCompare(m2.time as string),
    );
    plugin.setMarkers(markers);
  }, [data, alertsResp]);

  return (
    <div className="rounded-lg border border-zinc-200 p-2 dark:border-zinc-800">
      {isPending && (
        <p className="p-6 text-sm text-zinc-500" role="status">
          Loading chart…
        </p>
      )}
      {isError && (
        <div className="p-3">
          <ErrorMessage>
            Couldn't load prices:{" "}
            {error instanceof Error ? error.message : "unknown"}
          </ErrorMessage>
        </div>
      )}
      {data && data.bars.length === 0 && (
        <p className="p-6 text-sm text-zinc-500">
          No price data for {ticker} in this window. The Tiingo seed list may
          not include it yet.
        </p>
      )}
      <div
        ref={containerRef}
        className="h-[640px] w-full"
        data-testid="chart-container"
        aria-label={`Daily candlestick chart for ${ticker}`}
      />
    </div>
  );
}

// Create the lightweight-charts series for a single indicator key,
// populate it with data, and return the array of created series so
// the caller can track them for later removal. Returns an empty
// array if the indicators payload doesn't include this key (e.g.,
// the request was in flight when the user toggled — next render
// will catch up).
function createIndicatorSeries(
  chart: IChartApi,
  key: IndicatorKey,
  bars: DailyBar[],
  indicators: NonNullable<PricesRangeResponse["indicators"]>,
): ISeriesApi<"Line" | "Histogram">[] {
  const dates = bars.map((b) => b.date);
  const spec = INDICATOR_SPECS.find((s) => s.key === key);
  if (!spec) return [];

  // Scalar indicators on the candle pane.
  if (
    key === "sma_20" ||
    key === "sma_50" ||
    key === "sma_200" ||
    key === "ema_20" ||
    key === "ema_50" ||
    key === "ema_200" ||
    key === "vwap"
  ) {
    const values = indicators[key];
    if (!values) return [];
    const series = chart.addSeries(LineSeries, {
      color: spec.color,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    series.setData(zipScalar(dates, values));
    return [series];
  }

  if (key === "bb_20_2") {
    const values = indicators.bb_20_2;
    if (!values) return [];
    const upperData = zipScalar(
      dates,
      values.map((p) => p.upper),
    );
    const middleData = zipScalar(
      dates,
      values.map((p) => p.middle),
    );
    const lowerData = zipScalar(
      dates,
      values.map((p) => p.lower),
    );
    const upper = chart.addSeries(LineSeries, {
      color: "#a1a1aa",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const middle = chart.addSeries(LineSeries, {
      color: "#71717a",
      lineWidth: 1,
      lineStyle: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const lower = chart.addSeries(LineSeries, {
      color: "#a1a1aa",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    upper.setData(upperData);
    middle.setData(middleData);
    lower.setData(lowerData);
    return [upper, middle, lower];
  }

  if (key === "rsi_14") {
    const values = indicators.rsi_14;
    if (!values) return [];
    const series = chart.addSeries(
      LineSeries,
      {
        color: spec.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      },
      PANE_RSI,
    );
    series.setData(zipScalar(dates, values));
    series.createPriceLine({
      price: 70,
      color: "#a1a1aa",
      lineStyle: 2,
      lineWidth: 1,
      axisLabelVisible: true,
      title: "70",
    });
    series.createPriceLine({
      price: 30,
      color: "#a1a1aa",
      lineStyle: 2,
      lineWidth: 1,
      axisLabelVisible: true,
      title: "30",
    });
    return [series];
  }

  if (key === "macd_12_26_9") {
    const values = indicators.macd_12_26_9;
    if (!values) return [];
    const macdLine = chart.addSeries(
      LineSeries,
      {
        color: "#3b82f6",
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      },
      PANE_MACD,
    );
    const signalLine = chart.addSeries(
      LineSeries,
      {
        color: "#f97316", // orange-500
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      },
      PANE_MACD,
    );
    const histogram = chart.addSeries(
      HistogramSeries,
      {
        priceFormat: { type: "price", precision: 4, minMove: 0.0001 },
        priceLineVisible: false,
        lastValueVisible: false,
      },
      PANE_MACD,
    );
    macdLine.setData(macdSeriesToScalar(dates, values, "macd"));
    signalLine.setData(macdSeriesToScalar(dates, values, "signal"));
    histogram.setData(
      values
        .map((p, i) => ({
          time: dates[i]!,
          value: p.histogram,
          color: (p.histogram ?? 0) >= 0 ? `${UP_COLOR}80` : `${DOWN_COLOR}80`,
        }))
        .filter(
          (d): d is { time: string; value: number; color: string } =>
            d.value !== null && d.value !== undefined,
        ),
    );
    return [macdLine, signalLine, histogram];
  }

  if (key === "atr_14") {
    const values = indicators.atr_14;
    if (!values) return [];
    const series = chart.addSeries(
      LineSeries,
      {
        color: spec.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      },
      PANE_ATR,
    );
    series.setData(zipScalar(dates, values));
    return [series];
  }

  return [];
}

function zipScalar(
  dates: string[],
  values: IndicatorScalarSeries,
): { time: string; value: number }[] {
  const out: { time: string; value: number }[] = [];
  for (let i = 0; i < dates.length; i++) {
    const v = values[i];
    if (v === null || v === undefined) continue;
    out.push({ time: dates[i]!, value: v });
  }
  return out;
}

function buildFireMarker(alert: Alert, date: string): SeriesMarker<Time> {
  const direction = alert.direction ?? "info";
  const color =
    direction === "buy"
      ? UP_COLOR
      : direction === "sell"
        ? DOWN_COLOR
        : "#a1a1aa";
  // Buy alerts → green up-arrow below the bar (entry signal).
  // Sell alerts → red down-arrow above the bar (exit signal).
  // Info → gray dot above the bar.
  const shape =
    direction === "buy"
      ? "arrowUp"
      : direction === "sell"
        ? "arrowDown"
        : "circle";
  const position = direction === "sell" ? "aboveBar" : "belowBar";
  return {
    time: date as unknown as Time,
    position,
    color,
    shape,
    text: alert.title.slice(0, 30),
  };
}

function lookbackDaysForWindow(window: ChartWindow): number {
  switch (window) {
    case "3M":
      return 90;
    case "1Y":
      return 365;
    case "2Y":
    default:
      return 365; // alerts table only goes back ~365 days for the API
  }
}

function macdSeriesToScalar(
  dates: string[],
  values: IndicatorMacdPoint[],
  field: keyof IndicatorMacdPoint,
): { time: string; value: number }[] {
  const out: { time: string; value: number }[] = [];
  for (let i = 0; i < dates.length; i++) {
    const point = values[i];
    if (!point) continue;
    const v = point[field];
    if (v === null || v === undefined) continue;
    out.push({ time: dates[i]!, value: v });
  }
  return out;
}

// === News sidebar =======================================================

function NewsSidebar({ ticker }: { ticker: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["news", ticker, { limit: 20 }],
    queryFn: () => getNews(ticker, { limit: 20 }),
    staleTime: 60_000,
  });

  return (
    <section aria-labelledby="news-heading">
      <h3 id="news-heading" className="mb-3 text-sm font-medium uppercase tracking-wide text-zinc-500">
        News · {ticker}
      </h3>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load news:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.articles.length === 0 && (
        <p className="text-sm text-zinc-500">
          No news yet — Tiingo ingestion runs hourly on weekday market hours.
        </p>
      )}
      {data && data.articles.length > 0 && (
        <ul className="space-y-3">
          {uniqueByTitle(data.articles).map((a) => (
            <NewsItem key={a.tiingo_id} article={a} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** The same story often arrives from several outlets. Keep the first. */
function uniqueByTitle(articles: readonly NewsArticle[]): NewsArticle[] {
  const seen = new Set<string>();
  return articles.filter((a) => {
    const key = a.title.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function NewsItem({ article }: { article: NewsArticle }) {
  return (
    <li className="rounded-md border border-zinc-200 p-3 text-sm dark:border-zinc-800">
      <a
        href={article.url}
        target="_blank"
        rel="noreferrer noopener"
        className="block font-medium text-brand-600 hover:underline dark:text-brand-500"
      >
        {article.title}
      </a>
      <p className="mt-1 text-xs text-zinc-500">
        {article.source} · {new Date(article.published_date).toLocaleString()}
      </p>
      {article.description && (
        <p className="mt-2 line-clamp-3 text-xs text-zinc-600 dark:text-zinc-400">
          {article.description}
        </p>
      )}
    </li>
  );
}

// === Hooks =============================================================

function usePricesQuery(
  ticker: string,
  windowSel: ChartWindow,
  selected: Set<IndicatorKey>,
): {
  data: PricesRangeResponse | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
} {
  const today = etTodayIsoDate();
  const startDate = startDateForWindow(windowSel, today);
  // Always include atr_14 in the fetched indicators so the sidebar
  // <PositionSizer> can read live ATR even when the user has the ATR
  // pane toggled off. Rendering uses `selected` directly (this set is
  // unchanged) so the chart only draws what the toggle bar shows. Cost
  // is one extra indicator series per request (~1ms server compute,
  // ~1KB payload) — well worth a duplicated fetch + UI blink.
  const indicators = useMemo(
    () => {
      const augmented = new Set<IndicatorKey>(selected);
      augmented.add("atr_14");
      return [...augmented].sort();
    },
    // Set-equality: stringify for stable identity; `selected` is a Set
    // so React would re-fire on every reference change otherwise.
    [[...selected].sort().join(",")],
  );
  const indicatorsKey = indicators.join(",");
  const q = useQuery({
    queryKey: ["prices", ticker, startDate, today, indicatorsKey],
    queryFn: () => getPricesRange(ticker, startDate, today, indicators),
    staleTime: 60_000,
  });
  return {
    data: q.data,
    isPending: q.isPending,
    isError: q.isError,
    error: q.error,
  };
}
